import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';
import { Card, Table, BackLink, Input, Select, Button } from 'nhsuk-react-components';
import { FaCheck, FaClock, FaBan } from 'react-icons/fa';
import { getEmergencyAccessHistory, exportEmergencyAccessHistory } from '../../actions/emergencyAccessActions';
import Pagination from '../../components/pagination/Pagination.component';
import AppSpinner from '../../components/spinner/AppSpinner.component';
import PageWrapper from '../../components/layout/PageWrapper.component';

const DEFAULT_FILTER = {
    page: 1,
    status: 'all',
    filter: 'patientName',
    query: '',
    dateFrom: '',
    dateTo: '',
};

const formatRequestedBy = (token) => {
    const email = token.requestedByEmail?.trim();
    if (!email || email === 'unauthenticated') {
        return 'unauthenticated';
    }
    return email;
};

function EmergencyAccessHistoryScreen({ history }) {
    const dispatch = useDispatch();
    const [draftFilter, setDraftFilter] = useState(DEFAULT_FILTER);
    const [appliedFilter, setAppliedFilter] = useState(DEFAULT_FILTER);
    const [exporting, setExporting] = useState(false);

    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    const emergencyAccessHistory = useSelector((state) => state.emergencyAccessHistory);
    const { loading, error, tokens, totalPages, totalCount } = emergencyAccessHistory;

    useEffect(() => {
        document.title = 'Emergency Access History';

        if (!userInfo || !userInfo.roles?.includes('SuperUser')) {
            history.push('/unauthorized');
            return;
        }

        dispatch(getEmergencyAccessHistory(appliedFilter));
    }, [dispatch, userInfo, history, appliedFilter]);

    const updateDraft = (updates) => {
        setDraftFilter((prev) => ({ ...prev, ...updates }));
    };

    const handleSearch = () => {
        setAppliedFilter({ ...draftFilter, page: 1 });
    };

    const handleClearFilters = () => {
        setDraftFilter(DEFAULT_FILTER);
        setAppliedFilter(DEFAULT_FILTER);
    };

    const handlePageChange = (page) => {
        setAppliedFilter((prev) => ({ ...prev, page }));
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            await dispatch(exportEmergencyAccessHistory(appliedFilter));
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            setExporting(false);
        }
    };

    const getStatusBadge = (token) => {
        const now = new Date();
        const expiresAt = new Date(token.expiresAt);
        const isExpired = now > expiresAt;

        if (token.isRevoked) {
            return <span style={{ color: '#d5281b', fontWeight: 'bold' }}><FaBan /> Revoked</span>;
        }
        if (token.isUsed) {
            return <span style={{ color: '#007f3b', fontWeight: 'bold' }}><FaCheck /> Used</span>;
        }
        if (isExpired) {
            return <span style={{ color: '#f9bf07', fontWeight: 'bold' }}><FaClock /> Expired</span>;
        }
        return <span style={{ color: '#005eb8', fontWeight: 'bold' }}><FaClock /> Active</span>;
    };

    return (
        <div className="page-content">
            <Container fluid>
                <BackLink href="/dashboard" onClick={(e) => { e.preventDefault(); history.push('/dashboard'); }}>
                    Back to Dashboard
                </BackLink>

                <h1>Emergency Access History</h1>
                <p className="nhsuk-body-l">
                    View all emergency access requests. All access is logged for security and audit purposes.
                </p>

                {error && (
                    <div className="nhsuk-error-summary" aria-labelledby="error-summary-title" role="alert">
                        <h2 className="nhsuk-error-summary__title" id="error-summary-title">
                            Error
                        </h2>
                        <div className="nhsuk-error-summary__body">
                            <p>{error}</p>
                        </div>
                    </div>
                )}

                <Card>
                    <Card.Content>
                        <div className="nhsuk-grid-row nhsuk-u-margin-bottom-4">
                            <div className="nhsuk-grid-column-two-thirds">
                                <PageWrapper title="Access Requests" totalCount={totalCount || 0} loading={loading} />
                            </div>
                            <div className="nhsuk-grid-column-one-third" style={{ textAlign: 'right' }}>
                                <Button onClick={handleExport} disabled={exporting || loading}>
                                    {exporting ? 'Exporting…' : 'Export to CSV'}
                                </Button>
                            </div>
                        </div>

                        <div className="emergency-history-filters">
                            <Select
                                label="Status"
                                id="status-filter"
                                value={draftFilter.status}
                                onChange={(e) => updateDraft({ status: e.target.value })}
                            >
                                <Select.Option value="all">All statuses</Select.Option>
                                <Select.Option value="used">Used</Select.Option>
                                <Select.Option value="active">Active</Select.Option>
                                <Select.Option value="expired">Expired</Select.Option>
                                <Select.Option value="revoked">Revoked</Select.Option>
                            </Select>

                            <Select
                                label="Search by"
                                id="field-filter"
                                value={draftFilter.filter}
                                onChange={(e) => updateDraft({ filter: e.target.value })}
                            >
                                <Select.Option value="patientName">Patient name</Select.Option>
                                <Select.Option value="nhsNumber">NHS number</Select.Option>
                                <Select.Option value="requestedBy">Requested by</Select.Option>
                                <Select.Option value="organisation">Organisation</Select.Option>
                                <Select.Option value="reason">Reason</Select.Option>
                            </Select>

                            <Input
                                label="Search"
                                id="query-filter"
                                name="query"
                                type="text"
                                value={draftFilter.query}
                                onChange={(e) => updateDraft({ query: e.target.value })}
                            />

                            <Input
                                label="From"
                                id="date-from"
                                name="dateFrom"
                                type="date"
                                value={draftFilter.dateFrom}
                                onChange={(e) => updateDraft({ dateFrom: e.target.value })}
                            />

                            <Input
                                label="To"
                                id="date-to"
                                name="dateTo"
                                type="date"
                                value={draftFilter.dateTo}
                                onChange={(e) => updateDraft({ dateTo: e.target.value })}
                            />

                            <div className="emergency-history-filters__actions">
                                <label className="nhsuk-label emergency-history-filters__action-label" aria-hidden="true">
                                    Actions
                                </label>
                                <div className="emergency-history-filters__action-buttons">
                                    <Button onClick={handleSearch} disabled={loading}>
                                        {loading ? 'Searching…' : 'Search'}
                                    </Button>
                                    <Button className="nhsuk-button--secondary" onClick={handleClearFilters} disabled={loading}>
                                        Clear filters
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <AppSpinner />
                        ) : !tokens || tokens.length === 0 ? (
                            <p>No emergency access requests found.</p>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <Table responsive className="compact-admin-table">
                                    <Table.Head>
                                        <Table.Row>
                                            <Table.Cell>Status</Table.Cell>
                                            <Table.Cell>Patient</Table.Cell>
                                            <Table.Cell>NHS Number</Table.Cell>
                                            <Table.Cell>Requested By</Table.Cell>
                                            <Table.Cell>Organisation</Table.Cell>
                                            <Table.Cell>Reason</Table.Cell>
                                            <Table.Cell>IP Address</Table.Cell>
                                            <Table.Cell>Requested At</Table.Cell>
                                            <Table.Cell>Used At</Table.Cell>
                                        </Table.Row>
                                    </Table.Head>
                                    <Table.Body>
                                        {tokens.map((token) => (
                                            <Table.Row key={token.id}>
                                                <Table.Cell>{getStatusBadge(token)}</Table.Cell>
                                                <Table.Cell>{token.patientName}</Table.Cell>
                                                <Table.Cell>{token.patientNHSNumber}</Table.Cell>
                                                <Table.Cell>{formatRequestedBy(token)}</Table.Cell>
                                                <Table.Cell>{token.organisationName || 'N/A'}</Table.Cell>
                                                <Table.Cell>{token.reason || '-'}</Table.Cell>
                                                <Table.Cell>{token.ipAddress || '-'}</Table.Cell>
                                                <Table.Cell>
                                                    {new Date(token.requestedAt).toLocaleString('en-GB')}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {token.usedAt
                                                        ? new Date(token.usedAt).toLocaleString('en-GB')
                                                        : '-'
                                                    }
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            </div>
                        )}

                        <Pagination
                            currentPage={appliedFilter.page}
                            totalPages={totalPages || 1}
                            handlePageChange={handlePageChange}
                        />
                    </Card.Content>
                </Card>
            </Container>
        </div>
    );
}

export default EmergencyAccessHistoryScreen;
